// Schlanke Zustandsverwaltung der Editor-SPA über React-Context (keine schwere Lib).
//
// Hält das aktive Projekt, den geladenen Signalkatalog und -graphen sowie eine Toast-Liste für
// Erfolgs-/Fehlermeldungen (an die Statusleiste gespiegelt). Views ziehen sich das, was sie
// brauchen, über den `useStore`-Hook.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import type { PlantSchematic, ProjectSummary, SignalCatalog, SignalGraph } from '../api/types';
import { ApiError } from '../api/client';

export type ToastKind = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface StoreValue {
  // Projektauswahl
  projects: ProjectSummary[];
  setProjects: (p: ProjectSummary[]) => void;
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  activeProject: ProjectSummary | null;

  // geladene Projektdaten
  catalog: SignalCatalog | null;
  setCatalog: (c: SignalCatalog | null) => void;
  graph: SignalGraph | null;
  setGraph: (g: SignalGraph | null) => void;
  schematic: PlantSchematic | null;
  setSchematic: (s: PlantSchematic | null) => void;

  // Zähler, den Aktionen erhöhen, um ein Neuladen der aktiven Projektdaten anzustoßen (z. B. nach
  // einem Revert aus der Historie). Views/Effekte, die davon abhängen, laden dann neu.
  reloadNonce: number;
  bumpReload: () => void;

  // Meldungen
  toasts: Toast[];
  notify: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
  // bequemer Fehlerpfad: verdichtet ApiError/unbekannte Fehler zu einem Toast
  reportError: (err: unknown, fallback: string) => void;
}

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }): JSX.Element {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SignalCatalog | null>(null);
  const [graph, setGraph] = useState<SignalGraph | null>(null);
  const [schematic, setSchematic] = useState<PlantSchematic | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [reloadNonce, setReloadNonce] = useState(0);
  const nextId = useRef(1);

  const bumpReload = useCallback(() => setReloadNonce((n) => n + 1), []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      // Info/Erfolg blenden sich selbst aus; Fehler bleiben stehen, bis der Nutzer sie schließt.
      if (kind !== 'error') {
        window.setTimeout(() => dismiss(id), 4000);
      }
    },
    [dismiss],
  );

  const reportError = useCallback(
    (err: unknown, fallback: string) => {
      const message = err instanceof ApiError ? err.message : fallback;
      notify('error', message);
    },
    [notify],
  );

  const value = useMemo<StoreValue>(
    () => ({
      projects,
      setProjects,
      activeProjectId,
      setActiveProjectId,
      activeProject: projects.find((p) => p.id === activeProjectId) ?? null,
      catalog,
      setCatalog,
      graph,
      setGraph,
      schematic,
      setSchematic,
      reloadNonce,
      bumpReload,
      toasts,
      notify,
      dismiss,
      reportError,
    }),
    [
      projects,
      activeProjectId,
      catalog,
      graph,
      schematic,
      reloadNonce,
      bumpReload,
      toasts,
      notify,
      dismiss,
      reportError,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore muss innerhalb von <StoreProvider> stehen');
  return ctx;
}
